import { useEffect, useMemo, useRef, useState } from 'react'
import Map, { Marker, NavigationControl, Popup, MapRef } from 'react-map-gl'
import { faCity, faCompass, faMap } from '@fortawesome/pro-duotone-svg-icons'

import 'mapbox-gl/dist/mapbox-gl.css'

import Icon from '../icon/Icon'
import { DiscoverBuilding } from '../../_types/quickpass-discover'
import {
  formatBedSummary,
  formatMatchSummary,
  formatPriceRange,
  formatPriceRangeShort,
} from '../discover-building-card/helpers'
import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  computeBounds,
  hasValidCoordinates,
  matchScoreTone,
} from './helpers'
import {
  MapPlaceholder,
  MapShell,
  MarkerButton,
  MissingCoordsBanner,
  PopupActionButton,
  PopupActions,
  PopupImage,
  PopupImagePlaceholder,
  PopupMeta,
  PopupPrice,
  PopupRoot,
  PopupTitle,
} from './styled'
import { DiscoverMapProps } from './types'

const MAPBOX_STYLE = 'mapbox://styles/mapbox/streets-v12'

/**
 * Map view for /discover. One marker per building (matched units within
 * a building share the same marker). Click → popup with summary card and
 * a link to the building's detail page. Auto-fits to the filtered set
 * whenever buildings change.
 *
 * Falls back to a friendly placeholder when `VITE_MAPBOX_TOKEN` is unset
 * so the page still renders locally without leaking errors.
 *
 * @param {DiscoverMapProps} props
 * @returns {JSX.Element}
 */
const DiscoverMap = ({ buildings }: DiscoverMapProps): JSX.Element => {
  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN ?? ''
  const mapReference = useRef<MapRef | null>(null)
  const [openBuildingId, setOpenBuildingId] = useState<string | null>(null)

  const mappable = useMemo<DiscoverBuilding[]>(
    () => buildings.filter(hasValidCoordinates),
    [buildings],
  )

  const droppedCount = buildings.length - mappable.length

  useEffect(() => {
    if (mapReference.current === null) return
    if (mappable.length === 0) return

    const bounds = computeBounds(mappable)
    if (bounds === null) return

    mapReference.current.fitBounds(
      [
        [bounds.minLng, bounds.minLat],
        [bounds.maxLng, bounds.maxLat],
      ],
      {
        padding: 64,
        duration: 600,
        maxZoom: 14,
      },
    )
  }, [mappable])

  if (mapboxToken === '') {
    return (
      <MapShell>
        <MapPlaceholder>
          <Icon icon={faMap} label="Map" />
          <strong>Mapbox token missing</strong>
          <span>
            Set <code>VITE_MAPBOX_TOKEN</code> in your <code>.env.local</code>{' '}
            to enable the map view. The list view keeps working without it.
          </span>
        </MapPlaceholder>
      </MapShell>
    )
  }

  if (mappable.length === 0) {
    return (
      <MapShell>
        <MapPlaceholder>
          <Icon icon={faCompass} label="Compass" />
          <strong>No buildings with coordinates</strong>
          <span>
            Buildings need lat/lng to land on the map. Try loosening filters or
            switching to the list view.
          </span>
        </MapPlaceholder>
      </MapShell>
    )
  }

  const openBuilding = mappable.find(building => building.id === openBuildingId)

  return (
    <MapShell>
      <Map
        ref={mapReference}
        mapboxAccessToken={mapboxToken}
        mapStyle={MAPBOX_STYLE}
        initialViewState={{
          latitude: DEFAULT_CENTER.latitude,
          longitude: DEFAULT_CENTER.longitude,
          zoom: DEFAULT_ZOOM,
        }}
        style={{ width: '100%', height: '100%' }}
        onClick={() => setOpenBuildingId(null)}
      >
        <NavigationControl position="top-right" />

        {mappable.map(building => {
          const tone = matchScoreTone(building.matchScore)
          return (
            <Marker
              key={building.id}
              latitude={building.latitude}
              longitude={building.longitude}
              anchor="bottom"
              onClick={event => {
                event.originalEvent.stopPropagation()
                setOpenBuildingId(building.id)
              }}
            >
              <MarkerButton
                type="button"
                tone={tone}
                aria-label={`${building.buildingName} — ${building.matchScore.toString()}% match`}
                title={`${building.matchingListingsCount.toString()} listings · ${building.matchScore.toString()}% match`}
              >
                {formatPriceRangeShort(building)}
              </MarkerButton>
            </Marker>
          )
        })}

        {openBuilding !== undefined ? (
          <Popup
            latitude={openBuilding.latitude}
            longitude={openBuilding.longitude}
            anchor="bottom"
            offset={28}
            closeOnClick={false}
            closeOnMove={false}
            onClose={() => setOpenBuildingId(null)}
            maxWidth="280px"
          >
            <PopupRoot>
              {openBuilding.imageUrls[0] !== undefined ? (
                <PopupImage
                  src={openBuilding.imageUrls[0]}
                  alt={openBuilding.buildingName}
                  loading="lazy"
                />
              ) : (
                <PopupImagePlaceholder aria-hidden="true">
                  <Icon icon={faCity} label="No photo" />
                </PopupImagePlaceholder>
              )}
              <PopupTitle
                to={`/discover/${encodeURIComponent(openBuilding.id)}`}
              >
                {openBuilding.buildingName}
              </PopupTitle>
              <PopupPrice>{formatPriceRange(openBuilding)}</PopupPrice>
              <PopupMeta>{formatMatchSummary(openBuilding)}</PopupMeta>
              {formatBedSummary(openBuilding) !== '' ? (
                <PopupMeta>{formatBedSummary(openBuilding)}</PopupMeta>
              ) : null}
              {openBuilding.compiledAddress !== '' ? (
                <PopupMeta>{openBuilding.compiledAddress}</PopupMeta>
              ) : null}
              <PopupActions>
                <PopupActionButton
                  to={`/discover/${encodeURIComponent(openBuilding.id)}`}
                  isPrimary
                >
                  View building
                </PopupActionButton>
              </PopupActions>
            </PopupRoot>
          </Popup>
        ) : null}
      </Map>

      {droppedCount > 0 ? (
        <MissingCoordsBanner>
          {droppedCount.toString()} building{droppedCount === 1 ? '' : 's'}{' '}
          missing coordinates — switch to list view to see them.
        </MissingCoordsBanner>
      ) : null}
    </MapShell>
  )
}

export default DiscoverMap
